<template>
    <div class="otaserver-list">
        <h2>OTA 서버 URL 목록</h2>

        <!-- 메인 서버 URL 섹션 -->
        <div class="main-server">
            <h3>메인 서버</h3>
            <div class="server-card main">
                <div class="url-info">
                    <span class="url">{{ mainServer?.url || '설정된 메인 서버 없음' }}</span>
                    <span class="date">{{ mainServer?.date || '' }}</span>
                    <div v-if="checkResult && checkResult.url === mainServer?.url" :class="['ota-check-result', checkResult.ok ? 'ok' : 'fail']">
                        {{ checkResult.ok ? 'OTA 서버 연결됨' : (checkResult.message || '연결 실패') }}
                    </div>
                </div>
                <div class="actions">
                    <button v-if="mainServer" type="button" class="check-btn" :disabled="checkingUrl !== null" @click="checkOtaServer(mainServer.url)">
                        {{ checkingUrl === mainServer.url ? '확인 중…' : '동작 확인' }}
                    </button>
                    <button v-if="mainServer" @click="unsetMainServer" class="danger">메인 해제</button>
                </div>
            </div>
        </div>

        <!-- 서버 URL 목록 섹션 -->
        <div class="server-list">
            <h3>서버 목록</h3>
            <div v-for="server in sortedServers" :key="server.id" class="server-card">
                <div class="url-info">
                    <span class="url">{{ server.url }}</span>
                    <span class="date">{{ formatDate(server.date) }}</span>
                    <div v-if="checkResult && checkResult.url === server.url" :class="['ota-check-result', checkResult.ok ? 'ok' : 'fail']">
                        {{ checkResult.ok ? 'OTA 서버 연결됨' : (checkResult.message || '연결 실패') }}
                    </div>
                </div>
                <div class="actions">
                    <button type="button" class="check-btn" :disabled="checkingUrl !== null" @click="checkOtaServer(server.url)">
                        {{ checkingUrl === server.url ? '확인 중…' : '동작 확인' }}
                    </button>
                    <button v-if="!server.isMain" @click="setAsMain(server)" class="primary">
                        메인으로 설정
                    </button>
                    <button @click="deleteServer(server)" class="danger">삭제</button>
                </div>
            </div>
        </div>

        <!-- 새 서버 추가 폼 -->
        <div class="add-server">
            <input v-model="newServerUrl" placeholder="새 OTA 서버 URL 입력" @keyup.enter="addServer">
            <button @click="addServer" class="primary">추가</button>
        </div>
    </div>
</template>

<script>
export default {
    name: 'OtaserverUrlList',

    data() {
        return {
            servers: [],
            newServerUrl: '',
            checkingUrl: null,
            checkResult: null, // { url, ok, message }
        }
    },

    computed: {
        mainServer() {
            if (!Array.isArray(this.servers)) return null;
            // isMain이 boolean이거나 1/0 정수일 수 있으므로 둘 다 처리
            return this.servers.find(server => server.isMain === true || server.isMain === 1 || server.is_main === 1)
        },

        sortedServers() {
            if (!Array.isArray(this.servers)) return [];
            return [...this.servers]
                .filter(server => {
                    // isMain이 false이거나 0이거나 없는 경우만 필터링
                    const isMain = server.isMain !== undefined ? server.isMain : server.is_main;
                    return isMain !== true && isMain !== 1;
                })
                .sort((a, b) => {
                    const dateA = a.date || a.created_at;
                    const dateB = b.date || b.created_at;
                    return new Date(dateB) - new Date(dateA);
                })
        }
    },

    methods: {
        async fetchServers() {
            try {
                const response = await fetch('/api/ota-servers', {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });
                
                if (!response.ok) {
                    console.error('API 응답 오류:', response.status, response.statusText);
                    this.servers = [];
                    return;
                }
                
                const data = await response.json();
                console.log('OTA 서버 API 응답:', data);
                
                // 응답 형식에 따라 데이터 추출
                if (Array.isArray(data)) {
                    this.servers = data;
                } else if (data && Array.isArray(data.data)) {
                    // { success, message, data: [...], pagination } 형식
                    this.servers = data.data;
                } else {
                    console.error('서버 목록 응답 형식 오류:', data);
                    this.servers = [];
                }
                
                console.log('설정된 서버 목록:', this.servers);
            } catch (error) {
                console.error('서버 목록 가져오기 실패:', error);
                this.servers = [];
            }
        },

        async addServer() {
            if (!this.newServerUrl.trim()) return;

            try {
                const response = await fetch('/api/ota-servers', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify({
                        url: this.newServerUrl
                    })
                });

                const result = await response.json();
                
                if (response.ok) {
                    await this.fetchServers();
                    this.newServerUrl = '';
                } else {
                    // 백엔드에서 오는 에러 메시지 표시
                    const errorMessage = result.message || result.error || '서버 추가에 실패했습니다.';
                    alert(errorMessage);
                    console.error('서버 추가 실패:', result);
                }
            } catch (error) {
                console.error('서버 추가 실패:', error);
                alert('서버 추가 중 오류가 발생했습니다.');
            }
        },

        async setAsMain(server) {
            try {
                const response = await fetch(`/api/ota-servers/${server.id}/main`, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });

                if (response.ok) {
                    await this.fetchServers();
                }
            } catch (error) {
                console.error('메인 서버 설정 실패:', error);
            }
        },

        async unsetMainServer() {
            try {
                const response = await fetch(`/api/ota-servers/main`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });

                if (response.ok) {
                    await this.fetchServers();
                }
            } catch (error) {
                console.error('메인 서버 해제 실패:', error);
            }
        },

        async deleteServer(server) {
            if (!confirm('정말 삭제하시겠습니까?')) return;

            try {
                const response = await fetch(`/api/ota-servers/${server.id}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });

                if (response.ok) {
                    await this.fetchServers();
                }
            } catch (error) {
                console.error('서버 삭제 실패:', error);
            }
        },

        formatDate(dateString) {
            const date = new Date(dateString);
            return isNaN(date) ? '날짜 없음' : date.toLocaleString();
        },

        async checkOtaServer(url) {
            if (!url || typeof url !== 'string') return;
            this.checkingUrl = url;
            this.checkResult = null;
            try {
                const res = await fetch(`/api/ota-servers/check?url=${encodeURIComponent(url)}`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                });
                const data = await res.json().catch(() => ({}));
                this.checkResult = {
                    url,
                    ok: !!data.ok,
                    message: data.message,
                };
            } catch (err) {
                this.checkResult = {
                    url,
                    ok: false,
                    message: err.message || '연결 확인 실패',
                };
            } finally {
                this.checkingUrl = null;
            }
        },
    },

    mounted() {
        this.fetchServers()
    }
}
</script>

<style scoped>
.otaserver-list {
    padding: 20px;
    max-width: 800px;
    margin: 0 auto;
}

.main-server,
.server-list {
    margin-bottom: 30px;
}

.server-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    margin-bottom: 10px;
    border-radius: 8px;
    background-color: #f5f5f5;
}

.server-card.main {
    background-color: #e3f2fd;
    border: 2px solid #2196f3;
}

.url-info {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.url {
    font-weight: bold;
}

.date {
    font-size: 0.9em;
    color: #666;
}

.actions {
    display: flex;
    gap: 10px;
}

.add-server {
    display: flex;
    gap: 10px;
    margin-top: 20px;
}

input {
    flex: 1;
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
}

button {
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

button.primary {
    background-color: #2196f3;
    color: white;
}

button.danger {
    background-color: #f44336;
    color: white;
}

button.check-btn {
    background-color: #2196f3;
    color: white;
}

button.check-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

button:hover:not(:disabled) {
    opacity: 0.9;
}

.ota-check-result {
    margin-top: 6px;
    font-size: 0.9em;
    padding: 4px 8px;
    border-radius: 4px;
}

.ota-check-result.ok {
    background: #e8f5e9;
    color: #2e7d32;
}

.ota-check-result.fail {
    background: #ffebee;
    color: #c62828;
}
</style>